/**
 * NDJSON Generator - Generates NDJSON format product entities
 * 符合 OpenAI Product Feed Specification
 * @see https://developers.openai.com/commerce/specs/feed
 */

import { extractTriples } from './triplesExtractor.js';
import { buildUnifiedProduct } from './fieldMapper.js';

/**
 * Build a single product entity from CSV row
 * @param {Object} row - CSV row data
 * @param {number} index - Row index
 * @param {string} baseUrl - Formatted base URL
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {Object} - Product entity object following OpenAI Product Feed Specification
 */
export function buildProductEntity(row, index, baseUrl, platform = 'shopline') {
  const unified = buildUnifiedProduct(row, platform, index);

  // 构建 productUrl（OpenAI 的 link 字段）
  const productUrl = unified.path
    ? `${baseUrl}${unified.path}`
    : `${baseUrl}/products/${unified.semantic_id}`;

  // 解析库存状态
  const availability = unified.stock > 0 ? 'in_stock' : 'out_of_stock';

  // 处理图片：第一张为主图，其余为附加图
  const image_link = unified.images[0] || null;
  const additional_image_link = unified.images.length > 1 ? unified.images.slice(1) : [];

  // 提取变体选项到扁平字段
  const color = unified.options.find(o => o.name?.toLowerCase().includes('color'))?.value || null;
  const size = unified.options.find(o => o.name?.toLowerCase().includes('size'))?.value || null;

  // item_group_id: 使用 spu 或 id
  const item_group_id = unified.spu || unified.id;

  // offer_id: 组合 sku + price
  const offer_id = unified.sku ? `${unified.sku}-${unified.price}` : null;

  // 处理价格格式：添加货币代码
  const price = `${unified.price} USD`;
  const sale_price = unified.compare_at_price ? `${unified.compare_at_price} USD` : null;

  // 处理重量格式：合并数值和单位
  const weight = unified.weight ? `${unified.weight} ${unified.weight_unit || 'g'}` : null;

  // 处理 product_category：确保使用 > 分隔符
  const product_category = unified.product_category || 'Uncategorized';

  return {
    // ========== OpenAI Flags (Required) ==========
    enable_search: 'true',
    enable_checkout: 'false',

    // ========== Basic Product Data (Required) ==========
    id: unified.id,
    gtin: unified.barcode || null,
    mpn: unified.sku || null,
    title: unified.title,
    description: unified.description || '',
    link: productUrl,

    // ========== Item Information ==========
    condition: 'new',
    product_category: product_category,
    brand: unified.brand || '',
    material: 'Various', // 暂时使用默认值

    // 尺寸信息
    weight: weight,
    // length, width, height: 暂不支持（CSV 中无对应字段）

    // ========== Media ==========
    image_link: image_link,
    additional_image_link: additional_image_link,
    // video_link: 暂不支持

    // ========== Price & Promotions ==========
    price: price,
    sale_price: sale_price,
    // sale_price_effective_date: 暂不支持

    // ========== Availability & Inventory (Required) ==========
    availability: availability,
    inventory_quantity: unified.stock || 0,

    // ========== Variants ==========
    item_group_id: item_group_id,
    item_group_title: unified.title,
    color: color,
    size: size,
    // size_system: 暂不支持
    // gender: 暂不支持
    offer_id: offer_id,

    // ========== Fulfillment ==========
    // shipping: 暂时留空（用户决策）
    // delivery_estimate: 暂不支持

    // ========== Merchant Info (Required) ==========
    seller_name: unified.brand || 'Unknown', // 从 CSV Vendor 字段获取
    seller_url: baseUrl,
    // seller_privacy_policy: 暂不支持（需要用户配置）
    // seller_tos: 暂不支持（需要用户配置）

    // ========== Returns (Required) ==========
    // return_policy: 暂不支持（需要用户配置 URL）
    // return_window: 暂不支持（需要用户配置）

    // ========== 保留部分原有字段用于向后兼容 ==========
    sku: unified.sku,
    stock: unified.stock,
    url: productUrl,
    platform: unified.platform,
    updated_at: unified.updated_at,

    // ========== Semantic Data ==========
    triples: extractTriples(row, platform),

    // ========== Raw Data ==========
    raw: row
  };
}

/**
 * Generate NDJSON content
 * @param {Array} parsedData - Parsed CSV data
 * @param {string} baseUrl - Formatted base URL
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {string} - NDJSON content
 */
export function generateNdjson(parsedData, baseUrl, platform = 'shopline') {
  const entities = parsedData.map((row, index) =>
    buildProductEntity(row, index, baseUrl, platform)
  );
  return entities.map(entity => JSON.stringify(entity)).join('\n');
}
